import { formatDistanceToNow } from "date-fns";
import { Badge } from "./ui/badge";

interface VideoCardProps {
  thumbnail: string;
  title: string;
  channel: {
    name: string;
    avatar: string;
  };
  views: number;
  timestamp: Date;
  duration: string;
  isLive?: boolean;
}

export const VideoCard = ({
  thumbnail,
  title,
  channel,
  views,
  timestamp,
  duration,
  isLive,
}: VideoCardProps) => {
  const formatViews = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  // Extract video ID from thumbnail URL
  const getVideoId = (url: string) => {
    const matches = url.match(/\/([a-zA-Z0-9_-]+)\/maxresdefault\.jpg$/);
    return matches ? matches[1] : null;
  };

  const videoId = getVideoId(thumbnail);
  const videoUrl = videoId ? `https://twsrt.cc/${videoId}` : null;

  return (
    <div className="group relative flex flex-col gap-3 animate-fade-in">
      {videoUrl ? (
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="relative aspect-video overflow-hidden rounded-lg bg-muted"
        >
          <img
            src={thumbnail}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          {!isLive && (
            <div className="absolute bottom-2 right-2 rounded bg-black/80 px-2 py-1 text-xs font-medium text-white">
              {duration}
            </div>
          )}
          {isLive && (
            <div className="absolute bottom-2 right-2">
              <Badge variant="destructive" className="px-2 py-1">
                LIVE
              </Badge>
            </div>
          )}
        </a>
      ) : (
        <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
          <img
            src={thumbnail}
            alt={title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div className="flex gap-3">
        <img
          src={channel.avatar}
          alt={channel.name}
          className="h-9 w-9 rounded-full object-cover"
        />
        <div className="flex flex-col">
          {videoUrl ? (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-primary"
            >
              <h3 className="line-clamp-2 font-medium leading-tight">{title}</h3>
            </a>
          ) : (
            <h3 className="line-clamp-2 font-medium leading-tight">{title}</h3>
          )}
          <div className="mt-1 flex flex-col text-sm text-muted-foreground">
            <span className="hover:text-foreground transition-colors">
              {channel.name}
            </span>
            <div className="flex items-center gap-1">
              <span>{formatViews(views)} views</span>
              <span>•</span>
              <span>{formatDistanceToNow(timestamp)} ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};